import { useMutation, useQuery } from '@tanstack/react-query'
import { Dropdown, Select } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons';
import Button from 'components/Button'
import ErrorIndicator from 'components/ErrorIndicator'
import Spinner from 'components/Spinner'
import React, { useEffect, useState } from 'react'
import HTTPClient from 'services/HTTPClient'
import { GRADES, TOPICS, showErrorToast, showSuccessToast } from 'utils'

type Props = {}

export interface QuestionTemplate {
    id: string
    question: string
    option_1: string
    option_2: string
    option_3: string
    option_4: string
    answer: string
    topic: string
    grade: string
    options?: string[],
    is_verified: boolean
    is_reported: boolean
}


interface QuestionTemplatesResponse {
    data: QuestionTemplate[]
    meta: {
        pagination: {
            page: Number;
            pageSize: Number;
            pageCount: Number;
            total: Number
        }
    }
}


const useQuestionTemplates = (grade, topic) => {
    const url = `/api/question-templates?filters[template_type][$eq]=AI_GENERATED&filters[topic][$eq]=${topic}&filters[grade][$eq]=${grade}&filters[is_verified][$null]=true&pagination[limit]=500`
    return useQuery<QuestionTemplatesResponse>(['questionTemplates', grade, topic], async () => {
        let response = await HTTPClient.get().getJSONResult(url)
        return response
    });
};

export const useApprovedQuestionTemplatesCount = (grade, topic) => {
    const url = `/api/question-templates?filters[template_type][$eq]=AI_GENERATED&filters[topic][$eq]=${topic}&filters[grade][$eq]=${grade}&filters[is_verified][$eq]=true&pagination[limit]=0`
    return useQuery(['approvedQuestionTemplates', grade, topic], async () => {
        let response = await HTTPClient.get().getJSONResult(url)
        return response["meta"]["pagination"]["total"]
    });
};

export const useRejectedQuestionTemplatesCount = (grade, topic) => {
    const url = `/api/question-templates?filters[template_type][$eq]=AI_GENERATED&filters[topic][$eq]=${topic}&filters[grade][$eq]=${grade}&filters[is_verified][$eq]=false&pagination[limit]=0`
    return useQuery(['rejectedQuestionTemplates', grade, topic], async () => {
        let response = await HTTPClient.get().getJSONResult(url)
        return response["meta"]["pagination"]["total"]
    });
};

export const useQuestionTemplatesCount = (grade, topic) => {
    const url = `/api/question-templates?filters[template_type][$eq]=AI_GENERATED&filters[topic][$eq]=${topic}&filters[grade][$eq]=${grade}&pagination[limit]=0`
    return useQuery(['questionTemplatesCount', grade, topic], async () => {
        let response = await HTTPClient.get().getJSONResult(url)
        return response["meta"]["pagination"]["total"]
    });
};

function Option({ value }) {
    return (
        <div className='d-inline-block has-background-light px-3 py-1 mr-3'>{value}</div>
    )
}

const useMutateRecordStatus = (id) => {
    return useMutation(async (is_verified: boolean) => {
        const url = `/api/question-templates/${id}`
        let response = await HTTPClient.get().putDataForJSONResult(url, {
            is_verified
        })
        return response
    },)
}

export function QuestionTemplateView({ qt, index }: { qt: QuestionTemplate, index: number }) {
    const { isLoading, mutateAsync } = useMutateRecordStatus(qt.id)
    const [isVerified, setIsVerified] = useState(null)

    const record = async (is_verified) => {
        setIsVerified(is_verified)
        try {
            await mutateAsync(is_verified)
            showSuccessToast("Marked!")
            qt.is_verified = is_verified
        } catch (error: any) {
            showErrorToast(error.toString())
            setIsVerified(null)
        }
    }

    return <div className='card px-5 py-5 mb-4'>
        <div>
            {index + 1}: {qt.question} <br />
            <div className="between">
                <div>
                    <div className='my-2'>
                        <strong>Options:</strong>
                    </div>
                    <Option value={qt.option_1} />
                    <Option value={qt.option_2} />
                    <Option value={qt.option_3} />
                    <Option value={qt.option_4} />
                </div>
                <div>
                    <div className='my-2'>
                        <strong>Answer: </strong>
                        {qt.answer}
                    </div>
                </div>
            </div>
        </div>
        <div className='between'>
            <div className='mt-3 has-text-danger'>
                {qt.is_reported && <div>
                    <InfoCircleOutlined className='mr-1' />
                    This question was reported by an user
                </div>}
            </div>
            <div>
                {isVerified === null || isLoading
                    ? <>
                        <Button isLoading={isLoading && isVerified === false} className='mr-2' variant='is-danger' onClick={() => record(false)}>Reject</Button>
                        <Button isLoading={isLoading && isVerified === true} variant='is-success' onClick={() => record(true)}>Approve</Button>
                    </>
                    : <div className='has-text-success	'>
                        Successfully marked as {isVerified === true ? "Verfied" : "Rejected"}
                    </div>
                }
            </div>
        </div>

    </div>
}


export default function CurateQuestionsPage({ }: Props) {
    const [grade, setGrade] = useState("Grade 2")
    const [topic, setTopic] = useState("math")
    const { data: templatesResponse, isFetching, error, refetch } = useQuestionTemplates(grade, topic);
    const { data: approvedCount } = useApprovedQuestionTemplatesCount(grade, topic);

    useEffect(() => {
        refetch()
    }, [grade, topic, refetch])


    if (error || isFetching) {
        return <div className="column mx-4" style={{ overflow: "auto" }}>
            {isFetching ? <div className="center">
                <Spinner className='mt-6' />
            </div> : null}
            {error ? <ErrorIndicator error={error} /> : null}
        </div>
    }
    return (
        <div className='mx-0 px-5 py-5' style={{ height: "100vh", overflow: "auto" }}>
            <div className="between mb-4">
                <div>
                    <h4 className='is-size-4'>
                        There are <strong>{templatesResponse?.data?.length} questions</strong> pending review
                    </h4>
                    <small>{approvedCount || 0} questions approved</small>
                </div>
                <div>
                    <Select
                        className='mr-2'
                        value={grade}
                        style={{ width: 120 }}
                        onChange={(value) => {
                            console.log(value)
                            setGrade(value)
                        }}
                        options={GRADES}
                    />
                    <Select
                        value={topic}
                        style={{ width: 120 }}
                        onChange={(value) => {
                            console.log(value)
                            setTopic(value)
                        }}
                        options={TOPICS.map(t => ({ ...t, value: t.topic }))}
                    />
                </div>
            </div>
            {
                templatesResponse?.data?.map((qt, index) => {
                    return <QuestionTemplateView key={qt.id} qt={qt} index={index} />
                })
            }
        </div>
    )
}